import * as React from 'react';
import { BriefcaseBusiness, ShieldCheck, UserRound } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';

type AuthShellRole = 'coach' | 'client' | 'invite';

interface AuthShellProps {
  role: AuthShellRole;
  title: string;
  subtitle: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const ROLE_META: Record<
  AuthShellRole,
  {
    badge: string;
    eyebrow: string;
    summary: string;
    icon: React.ComponentType<{ className?: string }>;
    accent: string;
    spotlight: string;
  }
> = {
  coach: {
    badge: 'Coach Portal',
    eyebrow: 'Built for coaches',
    summary: 'Manage clients, programs, payments, and AI workflows from your coach workspace.',
    icon: BriefcaseBusiness,
    accent: 'from-sky-500/20 via-cyan-500/10 to-transparent dark:from-sky-500/15 dark:via-cyan-500/10',
    spotlight:
      'Create your coach account, sign in, and onboard from dedicated coach-only links you can share anywhere.',
  },
  client: {
    badge: 'Client Portal',
    eyebrow: 'Built for clients',
    summary: 'Access workouts, nutrition plans, progress, payments, and messages from your client workspace.',
    icon: UserRound,
    accent: 'from-violet-500/20 via-fuchsia-500/10 to-transparent dark:from-violet-500/15 dark:via-fuchsia-500/10',
    spotlight: 'Clients sign in here after their coach invites them. No open self-serve client registration.',
  },
  invite: {
    badge: 'Invite-Only Access',
    eyebrow: 'Coach invitation required',
    summary: 'Finish setting up your client account using the invite link your coach sent you.',
    icon: ShieldCheck,
    accent: 'from-emerald-500/20 via-teal-500/10 to-transparent dark:from-emerald-500/15 dark:via-teal-500/10',
    spotlight: 'This page only works from a valid invite link, which keeps client onboarding tied to a coach.',
  },
};

export function AuthShell({ role, title, subtitle, children, footer }: AuthShellProps) {
  const meta = ROLE_META[role];
  const RoleIcon = meta.icon;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${meta.accent}`} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-background/10 to-transparent" />

      <div className="fixed bottom-4 left-4 z-30">
        <ThemeToggle className="shadow-lg" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-6 py-12">
        <div className="grid w-full max-w-5xl items-center gap-10 lg:grid-cols-[1.05fr_minmax(0,460px)]">
          <section className="hidden lg:block">
            <div className="max-w-xl space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-4 py-2 text-sm font-medium text-muted-foreground backdrop-blur">
                <RoleIcon className="h-4 w-4" />
                <span>{meta.badge}</span>
              </div>

              <div className="space-y-4">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">{meta.eyebrow}</p>
                <h1 className="text-4xl font-bold tracking-tight text-foreground">{title}</h1>
                <p className="max-w-lg text-lg leading-8 text-muted-foreground">{subtitle}</p>
              </div>

              <div className="rounded-3xl border border-border bg-card/75 p-6 shadow-sm backdrop-blur">
                <p className="text-sm font-semibold text-foreground">{meta.summary}</p>
                <p className="mt-3 text-sm leading-6 text-muted-foreground">{meta.spotlight}</p>
              </div>
            </div>
          </section>

          <section className="w-full">
            <div className="mx-auto w-full max-w-md space-y-6">
              <div className="text-center lg:hidden">
                <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-medium text-muted-foreground">
                  <RoleIcon className="h-4 w-4" />
                  <span>{meta.badge}</span>
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">{title}</h1>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{subtitle}</p>
              </div>

              {children}

              {footer}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
